/**
 * A function to create a log object for the 'reviewsSectionLoaded' event (evid: 8001)
 *
 * Event description: Event triggered when reviews are loaded (product page loaded).
 *
 * @param {reviewsSectionLoadedParams} params - The parameters for this event
 * @property {string} layout_type - type of layout
 * @property {number} page_count - total pages count
 * @property {GUID} visitor_id - visitor_id
 * @property {number} reviews_count - total reviews count
 * @property {string} origin - product_page (when scrolled down) / deeplink (when page opened using deeplink)
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {number} avg_rating - avg_rating
 * @property {GUID} msid - site id - msid
 * @return {LogObject<reviewsSectionLoadedParams>} LogObject<reviewsSectionLoadedParams>
 */
export function reviewsSectionLoaded(params) {
    return { evid: 8001, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'leaveAReviewButtonClicked' event (evid: 8002)
 *
 * Event description: Event triggered when Leave a Review button is clicked
 *
 * @param {leaveAReviewButtonClickedParams} params - The parameters for this event
 * @property {string} referral_info - email_notification / null (if it is not from email notification)
 * @property {GUID} visitor_id - visitor_id
 * @property {string} origin - empty_state / product_page / collection_page
 * @property {string} author_role - owner, member, anonymous
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<leaveAReviewButtonClickedParams>} LogObject<leaveAReviewButtonClickedParams>
 */
export function leaveAReviewButtonClicked(params) {
    return { evid: 8002, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'reviewPublishedSuccessfully' event (evid: 8003)
 *
 * Event description: Event triggered when Review is successfully published
 *
 * @param {reviewPublishedSuccessfullyParams} params - The parameters for this event
 * @property {number} image_count - how many images were added
 * @property {GUID} review_id - review guid
 * @property {boolean} is_edited - true for edited review, false for new review
 * @property {string} referral_info - email_notification / null (if it is not from email notification)
 * @property {GUID} visitor_id - visitor_id
 * @property {boolean} is_verified - 1 - Review is verified / 0 - Review is not verified
 * @property {string} review_text - review_text
 * @property {string} review_title - review title
 * @property {number} reviews_count - 1 - single review publish / 2 and more - bulk action publish
 * @property {number} rating - how many stars
 * @property {string} author_role - owner, member, anonymous
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} approver_role - role of approver (NULL if review was published without approval)
 * @property {number} video_count - how many videos were added
 * @property {GUID} msid - site id - msid
 * @property {GUID} approved_by - approver_id (NULL if review was published without approval)
 * @property {string} origin - collection_page / product_page / moderation
 * @return {LogObject<reviewPublishedSuccessfullyParams>} LogObject<reviewPublishedSuccessfullyParams>
 */
export function reviewPublishedSuccessfully(params) {
    return { evid: 8003, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'replyButtonClicked' event (evid: 8004)
 *
 * Event description: Event triggered when Reply button is clicked
 *
 * @param {replyButtonClickedParams} params - The parameters for this event
 * @property {GUID} review_id - parent review id to reply to
 * @property {GUID} visitor_id - visitor_id
 * @property {string} author_role - owner / member
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<replyButtonClickedParams>} LogObject<replyButtonClickedParams>
 */
export function replyButtonClicked(params) {
    return { evid: 8004, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'helpfulButtonClicked' event (evid: 8005)
 *
 * Event description: Event triggered when upvote/downvote button is clicked
 *
 * @param {helpfulButtonClickedParams} params - The parameters for this event
 * @property {GUID} review_id - review id
 * @property {string} role - member / owner / anonymous
 * @property {GUID} visitor_id - visitor_id
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {number} downvotes_count - final downvotes_count
 * @property {number} upvotes_count - final upvotes_count
 * @property {GUID} msid - site id - msid
 * @property {string} action - upvote / downvote
 * @return {LogObject<helpfulButtonClickedParams>} LogObject<helpfulButtonClickedParams>
 */
export function helpfulButtonClicked(params) {
    return { evid: 8005, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'helpfulActionPublished' event (evid: 8006)
 *
 * Event description: Event triggered when upvote/downvote action is published
 *
 * @param {helpfulActionPublishedParams} params - The parameters for this event
 * @property {GUID} review_id - review_id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {number} downvotes_count - final downvotes_count
 * @property {number} upvotes_count - final upvotes_count
 * @property {GUID} msid - site id - msid
 * @property {string} action - upvote / downvote
 * @return {LogObject<helpfulActionPublishedParams>} LogObject<helpfulActionPublishedParams>
 */
export function helpfulActionPublished(params) {
    return { evid: 8006, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'showMoreClicked' event (evid: 8007)
 *
 * Event description: when user clicks Show more/Show less to expand review text
 *
 * @param {showMoreClickedParams} params - The parameters for this event
 * @property {GUID} review_id - parent review id to reply to
 * @property {GUID} visitor_id - visitor_id
 * @property {string} origin - main_screen / media_dialog
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @property {string} action_type - show_more / show_less
 * @return {LogObject<showMoreClickedParams>} LogObject<showMoreClickedParams>
 */
export function showMoreClicked(params) {
    return { evid: 8007, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'sortReviewsOpenActions' event (evid: 8008)
 *
 * Event description: Event triggered when Sort Reviews button is clicked first time
 *
 * @param {sortReviewsOpenActionsParams} params - The parameters for this event
 * @property {GUID} visitor_id - visitor_id
 * @property {number} total_reviews - total reviews count under product, group, event etc.
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<sortReviewsOpenActionsParams>} LogObject<sortReviewsOpenActionsParams>
 */
export function sortReviewsOpenActions(params) {
    return { evid: 8008, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'filterReviewsOpenActions' event (evid: 8009)
 *
 * Event description: Event triggered when Filter Reviews button is clicked first time(click or keyboard)
 *
 * @param {filterReviewsOpenActionsParams} params - The parameters for this event
 * @property {GUID} visitor_id - visitor_id
 * @property {number} total_reviews - total reviews count under product, group, event etc.
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<filterReviewsOpenActionsParams>} LogObject<filterReviewsOpenActionsParams>
 */
export function filterReviewsOpenActions(params) {
    return { evid: 8009, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'sortReviewsActionClick' event (evid: 8010)
 *
 * Event description: Event triggered when filtering option is selected
 *
 * @param {sortReviewsActionClickParams} params - The parameters for this event
 * @property {GUID} visitor_id - visitor_id
 * @property {number} total_reviews - total reviews count under product, group, event etc.
 * @property {string} sort_by - newest / oldest / helpful / highest_rated / lowest_rated
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<sortReviewsActionClickParams>} LogObject<sortReviewsActionClickParams>
 */
export function sortReviewsActionClick(params) {
    return { evid: 8010, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'filterReviewsActionClick' event (evid: 8011)
 *
 * Event description: Event triggered when action is clicked in Filter Reviews dropdown or by clicking on bars
 *
 * @param {filterReviewsActionClickParams} params - The parameters for this event
 * @property {string} filtered_by - all_stars / 1 / 2 / 3 / 4 / 5
 * @property {GUID} visitor_id - visitor_id
 * @property {number} total_reviews - total number of reviews
 * @property {string} origin - dropdown / bars
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<filterReviewsActionClickParams>} LogObject<filterReviewsActionClickParams>
 */
export function filterReviewsActionClick(params) {
    return { evid: 8011, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'dotsActionButtonOpenActions' event (evid: 8012)
 *
 * Event description: Event triggered after user clicks on 3 dots action button
 *
 * @param {dotsActionButtonOpenActionsParams} params - The parameters for this event
 * @property {GUID} review_id - review guid
 * @property {string} role - owner, member, anonymous
 * @property {GUID} visitor_id - visitor_id
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} type - review / reply
 * @property {GUID} msid - site id - msid
 * @return {LogObject<dotsActionButtonOpenActionsParams>} LogObject<dotsActionButtonOpenActionsParams>
 */
export function dotsActionButtonOpenActions(params) {
    return { evid: 8012, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'showMyReviewButtonClicked' event (evid: 8013)
 *
 * Event description: Event triggered when Show my Review button is clicked
 *
 * @param {showMyReviewButtonClickedParams} params - The parameters for this event
 * @property {GUID} review_id - review guid
 * @property {string} role - owner, member
 * @property {GUID} visitor_id - visitor_id
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<showMyReviewButtonClickedParams>} LogObject<showMyReviewButtonClickedParams>
 */
export function showMyReviewButtonClicked(params) {
    return { evid: 8013, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'dotsActionButtonActionClick' event (evid: 8014)
 *
 * Event description: Event triggered when clicking on one of the actions
 *
 * @param {dotsActionButtonActionClickParams} params - The parameters for this event
 * @property {GUID} review_id - review guid
 * @property {string} role - owner, member, anonymous
 * @property {GUID} visitor_id - visitor_id
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} type - review / reply
 * @property {GUID} msid - site id - msid
 * @property {string} action - edit, delete, copy_link
 * @return {LogObject<dotsActionButtonActionClickParams>} LogObject<dotsActionButtonActionClickParams>
 */
export function dotsActionButtonActionClick(params) {
    return { evid: 8014, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'reviewDeleted' event (evid: 8015)
 *
 * Event description: Event triggered when review is deleted
 *
 * @param {reviewDeletedParams} params - The parameters for this event
 * @property {GUID} review_id - review id
 * @property {string} role - owner / member
 * @property {GUID} visitor_id - visitor_id
 * @property {boolean} is_verified - 1 - Review is verified / 0 - Review is not verified
 * @property {number} rating - how many stars
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {number} downvotes_count - downvotes_count
 * @property {number} upvotes_count - upvotes_count
 * @property {GUID} msid - site id - msid
 * @return {LogObject<reviewDeletedParams>} LogObject<reviewDeletedParams>
 */
export function reviewDeleted(params) {
    return { evid: 8015, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'emptyStateShown' event (evid: 8016)
 *
 * Event description: Event triggered when empty state is shown (e.g. app loaded but no reviews )
 *
 * @param {emptyStateShownParams} params - The parameters for this event
 * @property {string} description - description of type of empty state
 * @property {GUID} visitor_id - visitor_id
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<emptyStateShownParams>} LogObject<emptyStateShownParams>
 */
export function emptyStateShown(params) {
    return { evid: 8016, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'clickOnXToCloseMediaDialog' event (evid: 8017)
 *
 * Event description: Event triggered when X is clicked and media dialog is closed
 *
 * @param {clickOnXToCloseMediaDialogParams} params - The parameters for this event
 * @property {GUID} review_id - review guid
 * @property {string} role - owner, member, anonymous
 * @property {GUID} visitor_id - visitor_id
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<clickOnXToCloseMediaDialogParams>} LogObject<clickOnXToCloseMediaDialogParams>
 */
export function clickOnXToCloseMediaDialog(params) {
    return { evid: 8017, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'mediaModalOpened' event (evid: 8018)
 *
 * Event description: When media dialog gets open
 *
 * @param {mediaModalOpenedParams} params - The parameters for this event
 * @property {number} image_count - total number of images
 * @property {GUID} review_id - review_id
 * @property {string} role - member / owner / anonymous
 * @property {number} symbol_count - total number of symbols in review text
 * @property {GUID} visitor_id - visitor_id
 * @property {number} rating - how many stars
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {number} downvotes_count - downvotes_count
 * @property {number} video_count - total number of videos
 * @property {number} upvotes_count - upvotes_count
 * @property {GUID} msid - site id - msid
 * @return {LogObject<mediaModalOpenedParams>} LogObject<mediaModalOpenedParams>
 */
export function mediaModalOpened(params) {
    return { evid: 8018, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'paginationClick' event (evid: 8019)
 *
 * Event description: Event triggered when pagination button (previous/next) is clicked
 *
 * @param {paginationClickParams} params - The parameters for this event
 * @property {number} total_pages - total pages
 * @property {string} filtered_by - all_stars / 1/2/3/4/5
 * @property {string} role - owner, member, anonymous
 * @property {GUID} visitor_id - visitor_id
 * @property {number} current_page_number - current page
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @property {string} action - previous / next
 * @return {LogObject<paginationClickParams>} LogObject<paginationClickParams>
 */
export function paginationClick(params) {
    return { evid: 8019, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'showAllReviewsClick' event (evid: 8020)
 *
 * Event description: Event triggered when Show all reviews button is clicked
 *
 * @param {showAllReviewsClickParams} params - The parameters for this event
 * @property {string} role - owner, member, anonymous
 * @property {GUID} visitor_id - visitor_id
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<showAllReviewsClickParams>} LogObject<showAllReviewsClickParams>
 */
export function showAllReviewsClick(params) {
    return { evid: 8020, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'publishReviewButtonClick' event (evid: 8021)
 *
 * Event description: Event triggered when Publish review button is click
 *
 * @param {publishReviewButtonClickParams} params - The parameters for this event
 * @property {number} image_count - how many images were added
 * @property {GUID} review_id - review guid for edit / null for new review
 * @property {boolean} is_edited - true for edited review, false for new review
 * @property {string} referral_info - email_notification / null (if it is not from email notification)
 * @property {GUID} visitor_id - visitor_id
 * @property {number} rating - how many stars
 * @property {string} author_role - owner, member, anonymous
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {number} video_count - how many videos were added
 * @property {GUID} msid - site id - msid
 * @property {string} origin - product_page / collection_page
 * @return {LogObject<publishReviewButtonClickParams>} LogObject<publishReviewButtonClickParams>
 */
export function publishReviewButtonClick(params) {
    return { evid: 8021, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'publishReplyButtonClick' event (evid: 8022)
 *
 * Event description: Event triggered when Publish Reply button is click
 *
 * @param {publishReplyButtonClickParams} params - The parameters for this event
 * @property {GUID} review_id - review guid for edit / null for new review
 * @property {boolean} is_edited - true for edited review, false for new review
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} parent_review_id - review guid for edit / null for new review
 * @property {number} rating - how many stars
 * @property {string} author_role - owner, member
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<publishReplyButtonClickParams>} LogObject<publishReplyButtonClickParams>
 */
export function publishReplyButtonClick(params) {
    return { evid: 8022, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'cancelCreatingReview' event (evid: 8023)
 *
 * Event description: event is sent when user cancels writing review
 *
 * @param {cancelCreatingReviewParams} params - The parameters for this event
 * @property {number} image_count - how many images were added
 * @property {GUID} review_id - review_id
 * @property {boolean} is_edited - true for edited review, false for new review
 * @property {GUID} visitor_id - visitor_id
 * @property {number} rating - 1/2/3/4/5
 * @property {string} author_role - member / owner / anonymous
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {number} video_count - how many videos were added
 * @property {GUID} msid - site id - msid
 * @return {LogObject<cancelCreatingReviewParams>} LogObject<cancelCreatingReviewParams>
 */
export function cancelCreatingReview(params) {
    return { evid: 8023, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'cancelCreatingReply' event (evid: 8024)
 *
 * Event description: event is sent when user cancels writing reply
 *
 * @param {cancelCreatingReplyParams} params - The parameters for this event
 * @property {GUID} review_id - review guid for edit / null for new review
 * @property {boolean} is_edited - true for edited review, false for new review
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} parent_review_id - parent review id
 * @property {number} rating - how many stars
 * @property {string} author_role - owner, member, anonymous
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<cancelCreatingReplyParams>} LogObject<cancelCreatingReplyParams>
 */
export function cancelCreatingReply(params) {
    return { evid: 8024, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'replyDeleted' event (evid: 8025)
 *
 * Event description: Event triggered when reply is deleted
 *
 * @param {replyDeletedParams} params - The parameters for this event
 * @property {GUID} review_id - review id
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} parent_review_id - parent review id
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<replyDeletedParams>} LogObject<replyDeletedParams>
 */
export function replyDeleted(params) {
    return { evid: 8025, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'imageAddedToReview' event (evid: 8026)
 *
 * Event description: Event triggered when image(s) are uploaded to review
 *
 * @param {imageAddedToReviewParams} params - The parameters for this event
 * @property {number} image_count - 1/2/3/4/5 (total image count in review)
 * @property {string} role - owner / member / anonymous
 * @property {GUID} visitor_id - visitor_id
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {number} video_count - 1/2/3/4/5 (total video count in review)
 * @property {GUID} msid - site id - msid
 * @return {LogObject<imageAddedToReviewParams>} LogObject<imageAddedToReviewParams>
 */
export function imageAddedToReview(params) {
    return { evid: 8026, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'imageDeletedFromReview' event (evid: 8027)
 *
 * Event description: Event triggered when image  is deleted from review
 *
 * @param {imageDeletedFromReviewParams} params - The parameters for this event
 * @property {string} role - owner / member / anonymous
 * @property {GUID} visitor_id - visitor_id
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<imageDeletedFromReviewParams>} LogObject<imageDeletedFromReviewParams>
 */
export function imageDeletedFromReview(params) {
    return { evid: 8027, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'replyPublishedSuccessfully' event (evid: 8028)
 *
 * Event description: Event triggered when Reply is successfully published
 *
 * @param {replyPublishedSuccessfullyParams} params - The parameters for this event
 * @property {GUID} review_id - reply guid
 * @property {boolean} is_edited - true for edited reply, false for new reply
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} parent_review_id - parent review id
 * @property {string} review_title - reply title
 * @property {number} rating - how many stars
 * @property {string} author_role - owner, member
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<replyPublishedSuccessfullyParams>} LogObject<replyPublishedSuccessfullyParams>
 */
export function replyPublishedSuccessfully(params) {
    return { evid: 8028, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'settingsClickOnMenuTab' event (evid: 8029)
 *
 * Event description: Event triggered when user clicks on one of main menu tab items
 *
 * @param {settingsClickOnMenuTabParams} params - The parameters for this event
 * @property {string} tab_name - Settings / Display / Texts / Design / Support
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - msid
 * @return {LogObject<settingsClickOnMenuTabParams>} LogObject<settingsClickOnMenuTabParams>
 */
export function settingsClickOnMenuTab(params) {
    return { evid: 8029, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'settingsSettingsMenuLoaded' event (evid: 8030)
 *
 * Event description: Event triggered after user clicks to open settings and settings menu loaded
 *
 * @param {settingsSettingsMenuLoadedParams} params - The parameters for this event
 * @property {string} component_name - review / rating summary
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - msid
 * @return {LogObject<settingsSettingsMenuLoadedParams>} LogObject<settingsSettingsMenuLoadedParams>
 */
export function settingsSettingsMenuLoaded(params) {
    return { evid: 8030, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'settingsClickOnSubMenuItem' event (evid: 8031)
 *
 * Event description: Event triggered when user clicks on one of sub-menu items to expand settings. E.g. Design -> Review Form
 *
 * @param {settingsClickOnSubMenuItemParams} params - The parameters for this event
 * @property {string} tab_name - ReviewForm, ReviewList, Header, WidgetBackground
 * @property {GUID} visitor_id - visitor_id
 * @property {string} group_name - group_name
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - msid
 * @return {LogObject<settingsClickOnSubMenuItemParams>} LogObject<settingsClickOnSubMenuItemParams>
 */
export function settingsClickOnSubMenuItem(params) {
    return { evid: 8031, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'settingsToggleSwitchClick' event (evid: 8032)
 *
 * Event description: Event triggered when user clicks and changes toggle switch value
 *
 * @param {settingsToggleSwitchClickParams} params - The parameters for this event
 * @property {boolean} toggle_state - final toggle state True - On / False - Off
 * @property {string} tab_name - tab_name
 * @property {string} element_name - toggle name e.g. title_text / body_text / add_media_field / sort / filter /
 * @property {GUID} visitor_id - visitor_id
 * @property {string} group_name - group_name
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<settingsToggleSwitchClickParams>} LogObject<settingsToggleSwitchClickParams>
 */
export function settingsToggleSwitchClick(params) {
    return { evid: 8032, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'settingsSliderValueChange' event (evid: 8033)
 *
 * Event description: Event triggered when user changes slider value
 *
 * @param {settingsSliderValueChangeParams} params - The parameters for this event
 * @property {number} slider_value - final slider value
 * @property {string} tab_name - Settings / Display / Texts / Design / Support
 * @property {string} element_name - title_max_character_limit /  body_max_character_limit / max_media_item_count / review_count_per_page
 * @property {GUID} visitor_id - visitor_id
 * @property {string} group_name - group_name
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<settingsSliderValueChangeParams>} LogObject<settingsSliderValueChangeParams>
 */
export function settingsSliderValueChange(params) {
    return { evid: 8033, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'settingsCheckboxValueChange' event (evid: 8034)
 *
 * Event description: Event triggered when user changes checkbox value
 *
 * @param {settingsCheckboxValueChangeParams} params - The parameters for this event
 * @property {string} tab_name - Settings / Display / Texts / Design / Support
 * @property {string} element_name - element_name
 * @property {GUID} visitor_id - visitor_id
 * @property {string} group_name - group_name
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {boolean} checkbox_state - final checkbox value True / False
 * @property {GUID} msid - site id - msid
 * @return {LogObject<settingsCheckboxValueChangeParams>} LogObject<settingsCheckboxValueChangeParams>
 */
export function settingsCheckboxValueChange(params) {
    return { evid: 8034, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'settingsDropdownValueChange' event (evid: 8035)
 *
 * Event description: Event triggered when user changes dropdown value
 *
 * @param {settingsDropdownValueChangeParams} params - The parameters for this event
 * @property {string} dropdown_value - final dropdown value
 * @property {string} tab_name - Settings / Display / Texts / Design / Support
 * @property {string} element_name - media_types / default_sorting /
 * @property {GUID} visitor_id - visitor_id
 * @property {string} group_name - group_name
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - msid
 * @return {LogObject<settingsDropdownValueChangeParams>} LogObject<settingsDropdownValueChangeParams>
 */
export function settingsDropdownValueChange(params) {
    return { evid: 8035, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'settingsTextfieldValueChange' event (evid: 8036)
 *
 * Event description: Event triggered when user changes textfield value
 *
 * @param {settingsTextfieldValueChangeParams} params - The parameters for this event
 * @property {string} tab_name - Settings / Display / Texts / Design / Support
 * @property {string} element_name - element_name
 * @property {GUID} visitor_id - visitor_id
 * @property {string} text - final text value
 * @property {string} group_name - group_name
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<settingsTextfieldValueChangeParams>} LogObject<settingsTextfieldValueChangeParams>
 */
export function settingsTextfieldValueChange(params) {
    return { evid: 8036, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'settingsReturnAllToDefaultsClick' event (evid: 8037)
 *
 * Event description: Event triggered after user resets settings to defaults
 *
 * @param {settingsReturnAllToDefaultsClickParams} params - The parameters for this event
 * @property {string} tab_name - tab_name
 * @property {GUID} visitor_id - visitor_id
 * @property {string} group_name - group_name
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<settingsReturnAllToDefaultsClickParams>} LogObject<settingsReturnAllToDefaultsClickParams>
 */
export function settingsReturnAllToDefaultsClick(params) {
    return { evid: 8037, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'settingsColourChange' event (evid: 8038)
 *
 * Event description: Event triggered when user changes colour
 *
 * @param {settingsColourChangeParams} params - The parameters for this event
 * @property {string} tab_name - Settings / Display / Texts / Design / Support
 * @property {string} element_name - breakdown_numbers / progress_bar
 * @property {GUID} visitor_id - visitor_id
 * @property {string} group_name - group_name
 * @property {string} colour - final colour value
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<settingsColourChangeParams>} LogObject<settingsColourChangeParams>
 */
export function settingsColourChange(params) {
    return { evid: 8038, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'settingsFontChange' event (evid: 8039)
 *
 * Event description: Event triggered when user changes font
 *
 * @param {settingsFontChangeParams} params - The parameters for this event
 * @property {string} html_tag_name - html tag name for SEO and Accessibility
 * @property {string} tab_name - Settings / Display / Texts / Design / Support
 * @property {string} element_name - breakdown_numbers / progress_bar
 * @property {GUID} visitor_id - visitor_id
 * @property {string} preset_name - preset name e.g. "Custom"
 * @property {string} font - final font name
 * @property {string} group_name - group_name
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} font_size - final font size
 * @property {GUID} msid - site id - msid
 * @return {LogObject<settingsFontChangeParams>} LogObject<settingsFontChangeParams>
 */
export function settingsFontChange(params) {
    return { evid: 8039, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmReviewsSectionLoaded' event (evid: 8040)
 *
 * Event description: Event triggered when reviews  list come into BM view
 *
 * @param {bmReviewsSectionLoadedParams} params - The parameters for this event
 * @property {string} tab_name - tab_name
 * @property {number} reviews_count - total reviews count
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmReviewsSectionLoadedParams>} LogObject<bmReviewsSectionLoadedParams>
 */
export function bmReviewsSectionLoaded(params) {
    return { evid: 8040, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmClickOnTab' event (evid: 8041)
 *
 * Event description: Event triggered when user clicks on tab
 *
 * @param {bmClickOnTabParams} params - The parameters for this event
 * @property {string} target_tab_name - name of target tab
 * @property {string} tab_name - name of current tab
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmClickOnTabParams>} LogObject<bmClickOnTabParams>
 */
export function bmClickOnTab(params) {
    return { evid: 8041, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmItemSelectionToggled' event (evid: 8042)
 *
 * Event description: Sent when a user select/deselects an item(s) in a list
 *
 * @param {bmItemSelectionToggledParams} params - The parameters for this event
 * @property {number} selected_count - number of reviews selected (after action)
 * @property {boolean} is_select_all - Did the user click the "Select All" check box (will get "true" regardless the label attached to the check box: "Select All" or "Deselect All").
 * @property {string} click_type - The click purpose : "select" / "deselect"
 * @property {string} tab_name - name of current tab
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmItemSelectionToggledParams>} LogObject<bmItemSelectionToggledParams>
 */
export function bmItemSelectionToggled(params) {
    return { evid: 8042, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmPublishReview' event (evid: 8043)
 *
 * Event description: Sent when a user clicks PUBLISH button
 *
 * @param {bmPublishReviewParams} params - The parameters for this event
 * @property {string} review_id - review guid or csv for bulk action
 * @property {boolean} is_select_all - Are all reviews are selected?
 * @property {string} tab_name - name of current tab
 * @property {number} reviews_count - when all are selected
 * @property {string} origin - list / list_all / review_page
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmPublishReviewParams>} LogObject<bmPublishReviewParams>
 */
export function bmPublishReview(params) {
    return { evid: 8043, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmMoveToTrash' event (evid: 8044)
 *
 * Event description: Sent when a user clicks move to Trash button
 *
 * @param {bmMoveToTrashParams} params - The parameters for this event
 * @property {string} review_id - review_id
 * @property {boolean} is_select_all - Are all reviews are selected?
 * @property {string} tab_name - name of current tab
 * @property {boolean} is_verified - 1 - Review is verified / 0 - Review is not verified
 * @property {number} reviews_count - when all are selected otherwise 1
 * @property {string} origin - list / list_all / review_page
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmMoveToTrashParams>} LogObject<bmMoveToTrashParams>
 */
export function bmMoveToTrash(params) {
    return { evid: 8044, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmReplyAndPublishClick' event (evid: 8045)
 *
 * Event description: Sent when a user clicks Reply and Publish button
 *
 * @param {bmReplyAndPublishClickParams} params - The parameters for this event
 * @property {string} tab_name - name of current tab
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmReplyAndPublishClickParams>} LogObject<bmReplyAndPublishClickParams>
 */
export function bmReplyAndPublishClick(params) {
    return { evid: 8045, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmDeleteReview' event (evid: 8046)
 *
 * Event description: BM: Delete review.
 *
 * @param {bmDeleteReviewParams} params - The parameters for this event
 * @property {string} review_id - review_id or list of reiview_ids
 * @property {boolean} is_select_all - true if all reviews were selected
 * @property {string} tab_name - tab_name
 * @property {number} reviews_count - reviews count for bulk action
 * @property {string} origin - review_page / list / three_dot_action
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmDeleteReviewParams>} LogObject<bmDeleteReviewParams>
 */
export function bmDeleteReview(params) {
    return { evid: 8046, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmOpenFullReviewPage' event (evid: 8047)
 *
 * Event description: Sent when a user clicks on review to open full review page
 *
 * @param {bmOpenFullReviewPageParams} params - The parameters for this event
 * @property {string} review_id - review guid
 * @property {string} tab_name - name of current tab
 * @property {string} origin - list / three_dot_action
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmOpenFullReviewPageParams>} LogObject<bmOpenFullReviewPageParams>
 */
export function bmOpenFullReviewPage(params) {
    return { evid: 8047, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmThreeDotActionClickOnAction' event (evid: 8049)
 *
 * Event description: Sent when a user clicks on action inside three dot action menu
 *
 * @param {bmThreeDotActionClickOnActionParams} params - The parameters for this event
 * @property {string} review_id - review guid
 * @property {string} tab_name - tab_name
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @property {string} action_type - show_full / go_to_live / move_to_trash / delete / publish, reply (mobile), edit_reply, delete_reply
 * @property {string} origin - review / reply
 * @return {LogObject<bmThreeDotActionClickOnActionParams>} LogObject<bmThreeDotActionClickOnActionParams>
 */
export function bmThreeDotActionClickOnAction(params) {
    return { evid: 8049, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmClickOnProductNameOpenProductPage' event (evid: 8050)
 *
 * Event description: Sent when a user clicks on product name to open product page
 *
 * @param {bmClickOnProductNameOpenProductPageParams} params - The parameters for this event
 * @property {string} review_id - review guid
 * @property {string} tab_name - name of current tab
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmClickOnProductNameOpenProductPageParams>} LogObject<bmClickOnProductNameOpenProductPageParams>
 */
export function bmClickOnProductNameOpenProductPage(params) {
    return { evid: 8050, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmSubmitReply' event (evid: 8051)
 *
 * Event description: Sent when a user enters text and presses Reply button
 *
 * @param {bmSubmitReplyParams} params - The parameters for this event
 * @property {string} review_id - review guid
 * @property {boolean} is_edited - true when editing reply
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmSubmitReplyParams>} LogObject<bmSubmitReplyParams>
 */
export function bmSubmitReply(params) {
    return { evid: 8051, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmCancelWritingReply' event (evid: 8052)
 *
 * Event description: Sent when a user closes window
 *
 * @param {bmCancelWritingReplyParams} params - The parameters for this event
 * @property {string} review_id - review guid
 * @property {string} origin - cancel / X / Update
 * @property {boolean} is_editing - TRUE / FALSE (when cancelation is done in Edit mode, then TRUE else FALSE)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmCancelWritingReplyParams>} LogObject<bmCancelWritingReplyParams>
 */
export function bmCancelWritingReply(params) {
    return { evid: 8052, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmDeleteReply' event (evid: 8053)
 *
 * Event description: Sent when a user confirms deleting reply
 *
 * @param {bmDeleteReplyParams} params - The parameters for this event
 * @property {string} review_id - review guid
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmDeleteReplyParams>} LogObject<bmDeleteReplyParams>
 */
export function bmDeleteReply(params) {
    return { evid: 8053, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmClickOnSettingsButton' event (evid: 8054)
 *
 * Event description: Sent when a user clicks on Settings button
 *
 * @param {bmClickOnSettingsButtonParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmClickOnSettingsButtonParams>} LogObject<bmClickOnSettingsButtonParams>
 */
export function bmClickOnSettingsButton(params) {
    return { evid: 8054, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmOpenFiltersPanel' event (evid: 8055)
 *
 * Event description: Sent when a user clicks on Filter button
 *
 * @param {bmOpenFiltersPanelParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @property {string} current_filters - The current state of filters, following the filter trigger
 * @return {LogObject<bmOpenFiltersPanelParams>} LogObject<bmOpenFiltersPanelParams>
 */
export function bmOpenFiltersPanel(params) {
    return { evid: 8055, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmFiltersApplied' event (evid: 8056)
 *
 * Event description: Sent when a user changes filtering
 *
 * @param {bmFiltersAppliedParams} params - The parameters for this event
 * @property {string} tab_name - current tab name
 * @property {string} origin - filter_panel / reviews_list
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @property {string} action_type - "add"/"remove"
 * @property {string} current_filters - The current state of filters, following the filter trigger
 * @return {LogObject<bmFiltersAppliedParams>} LogObject<bmFiltersAppliedParams>
 */
export function bmFiltersApplied(params) {
    return { evid: 8056, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmClearFilters' event (evid: 8057)
 *
 * Event description: Sent when a user clears filters
 *
 * @param {bmClearFiltersParams} params - The parameters for this event
 * @property {string} tab_name - current tab name
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmClearFiltersParams>} LogObject<bmClearFiltersParams>
 */
export function bmClearFilters(params) {
    return { evid: 8057, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmCloseFiltersPanel' event (evid: 8058)
 *
 * Event description: Sent when a user closes filters panel
 *
 * @param {bmCloseFiltersPanelParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @property {string} current_filters - The current state of filters, following the filter trigger
 * @return {LogObject<bmCloseFiltersPanelParams>} LogObject<bmCloseFiltersPanelParams>
 */
export function bmCloseFiltersPanel(params) {
    return { evid: 8058, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmSettingsDiscardChanges' event (evid: 8059)
 *
 * Event description: Sent when a user discards new changes
 *
 * @param {bmSettingsDiscardChangesParams} params - The parameters for this event
 * @property {string} section_name - main / form / moderation / email
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmSettingsDiscardChangesParams>} LogObject<bmSettingsDiscardChangesParams>
 */
export function bmSettingsDiscardChanges(params) {
    return { evid: 8059, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmSettingsPressEditOnSectionSrc69Evid8060' event (evid: 8060)
 *
 * Event description: Sent when a user saves new settings after changing something
 *
 * @param {bmSettingsPressEditOnSectionSrc69Evid8060Params} params - The parameters for this event
 * @property {string} section_name - form / moderation / email
 * @property {string} settings_before - json of all old settings in section
 * @property {string} settings_after - json of all new settings in section
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmSettingsPressEditOnSectionSrc69Evid8060Params>} LogObject<bmSettingsPressEditOnSectionSrc69Evid8060Params>
 */
export function bmSettingsPressEditOnSectionSrc69Evid8060(params) {
    return { evid: 8060, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'showReviewApprovalModal' event (evid: 8061)
 *
 * Event description: Event triggered when review is sent to approval and informational modal is shown
 *
 * @param {showReviewApprovalModalParams} params - The parameters for this event
 * @property {GUID} review_id - review guid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<showReviewApprovalModalParams>} LogObject<showReviewApprovalModalParams>
 */
export function showReviewApprovalModal(params) {
    return { evid: 8061, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'closeReviewApprovalModal' event (evid: 8062)
 *
 * Event description: Event triggered when user closes modal window
 *
 * @param {closeReviewApprovalModalParams} params - The parameters for this event
 * @property {GUID} review_id - review_id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} origin - X / OK
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<closeReviewApprovalModalParams>} LogObject<closeReviewApprovalModalParams>
 */
export function closeReviewApprovalModal(params) {
    return { evid: 8062, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmNextPreviousReviewClick' event (evid: 8063)
 *
 * Event description: Sent when a user clicks on arrow button when full review page is opened
 *
 * @param {bmNextPreviousReviewClickParams} params - The parameters for this event
 * @property {string} direction - previous (<-) / next (->)
 * @property {number} reviews_count - total number of reviews
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmNextPreviousReviewClickParams>} LogObject<bmNextPreviousReviewClickParams>
 */
export function bmNextPreviousReviewClick(params) {
    return { evid: 8063, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmSettingsPressEditOnSection' event (evid: 8064)
 *
 * Event description: Sent when a user wants to change settings in specific section and presses Edit button
 *
 * @param {bmSettingsPressEditOnSectionParams} params - The parameters for this event
 * @property {string} section_name - main / form / moderation / email
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmSettingsPressEditOnSectionParams>} LogObject<bmSettingsPressEditOnSectionParams>
 */
export function bmSettingsPressEditOnSection(params) {
    return { evid: 8064, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'newReviewSubmittedForApproval' event (evid: 8065)
 *
 * Event description: Event triggered after Review is submitted for approval
 *
 * @param {newReviewSubmittedForApprovalParams} params - The parameters for this event
 * @property {number} image_count - how many images were added
 * @property {GUID} review_id - review guid
 * @property {string} referral_info - email_notification / null (if it is not from email notification)
 * @property {GUID} visitor_id - visitor_id
 * @property {string} review_text - review_text
 * @property {string} review_title - review title
 * @property {number} rating - how many stars
 * @property {string} author_role - owner, member, anonymous
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {number} video_count - how many videos were added
 * @property {GUID} msid - msid
 * @property {string} origin - product_page / collection_page
 * @return {LogObject<newReviewSubmittedForApprovalParams>} LogObject<newReviewSubmittedForApprovalParams>
 */
export function newReviewSubmittedForApproval(params) {
    return { evid: 8065, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'intentClickIntoReviewSection' event (evid: 8067)
 *
 * Event description: Event is triggered when user / UoU click into reviews section
 *
 * @param {intentClickIntoReviewSectionParams} params - The parameters for this event
 * @property {GUID} visitor_id - visitor_id
 * @property {string} origin - empty_state / product_page / collection_page
 * @property {string} author_role - owner, member, anonymous
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<intentClickIntoReviewSectionParams>} LogObject<intentClickIntoReviewSectionParams>
 */
export function intentClickIntoReviewSection(params) {
    return { evid: 8067, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmReplyButtonClick' event (evid: 8068)
 *
 * Event description: Sent when a user clicks Reply button in BM when review window is opened
 *
 * @param {bmReplyButtonClickParams} params - The parameters for this event
 * @property {string} review_id - review guid
 * @property {string} tab_name - published / pending / rejected
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @property {string} origin - full_review / list
 * @return {LogObject<bmReplyButtonClickParams>} LogObject<bmReplyButtonClickParams>
 */
export function bmReplyButtonClick(params) {
    return { evid: 8068, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'reviewsSectionLoadedInViewport' event (evid: 8069)
 *
 * Event description: Reviews section loaded in viewport.
 *
 * @param {reviewsSectionLoadedInViewportParams} params - The parameters for this event
 * @property {string} layout_type - type of layout
 * @property {number} page_count - total pages count
 * @property {GUID} visitor_id - visitor_id
 * @property {number} reviews_count - total reviews count
 * @property {string} origin - product_page (when scrolled down) / deeplink (when page opened using deeplink) / collection_page
 * @property {string} parent_resource_id - review parent entity GUID (product, group, event etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {number} avg_rating - avg_rating
 * @property {GUID} msid - site id - msid
 * @return {LogObject<reviewsSectionLoadedInViewportParams>} LogObject<reviewsSectionLoadedInViewportParams>
 */
export function reviewsSectionLoadedInViewport(params) {
    return { evid: 8069, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'reviewSubmissionWarning' event (evid: 8070)
 *
 * Event description: Event triggered after Review is trying to be published, but not all necessary fields are filled.
 *
 * @param {reviewSubmissionWarningParams} params - The parameters for this event
 * @property {GUID} review_id - review guid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} warning_reason - name / email / add_a_rating / review_title / review / add_images_videos
 * @property {string} author_role - owner, member, anonymous
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - msid
 * @property {string} origin - collection_page / product_page
 * @return {LogObject<reviewSubmissionWarningParams>} LogObject<reviewSubmissionWarningParams>
 */
export function reviewSubmissionWarning(params) {
    return { evid: 8070, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'reviewsPluginAdded' event (evid: 8071)
 *
 * Event description: Event triggered when plugin(widget) is added
 *
 * @param {reviewsPluginAddedParams} params - The parameters for this event
 * @property {string} uuid - uuid
 * @property {string} site_builder_type - site_builder type: Editor, editor_x
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} plugin_id - plugin id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<reviewsPluginAddedParams>} LogObject<reviewsPluginAddedParams>
 */
export function reviewsPluginAdded(params) {
    return { evid: 8071, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmEditReplyButtonClick' event (evid: 8072)
 *
 * Event description: Sent when a user clicks Edit reply button in BM when review window is opened
 *
 * @param {bmEditReplyButtonClickParams} params - The parameters for this event
 * @property {string} review_id - review guid
 * @property {string} tab_name - published / pending / rejected
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmEditReplyButtonClickParams>} LogObject<bmEditReplyButtonClickParams>
 */
export function bmEditReplyButtonClick(params) {
    return { evid: 8072, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmDeleteIconClick' event (evid: 8073)
 *
 * Event description: Sent when a user clicks Delete icon in BM when review window is opened
 *
 * @param {bmDeleteIconClickParams} params - The parameters for this event
 * @property {string} review_id - review guid
 * @property {string} tab_name - published / pending / rejected
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmDeleteIconClickParams>} LogObject<bmDeleteIconClickParams>
 */
export function bmDeleteIconClick(params) {
    return { evid: 8073, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmReviewSReplyIntentClick' event (evid: 8074)
 *
 * Event description: Sent when a user starts writing a reply in BM when review modal is opened
 *
 * @param {bmReviewSReplyIntentClickParams} params - The parameters for this event
 * @property {string} review_id - review guid
 * @property {string} tab_name - published / pending / rejected
 * @property {boolean} is_editing - TRUE when cursor is placed in editing mode, / FALSE
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmReviewSReplyIntentClickParams>} LogObject<bmReviewSReplyIntentClickParams>
 */
export function bmReviewSReplyIntentClick(params) {
    return { evid: 8074, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmReplySubmissionWarning' event (evid: 8076)
 *
 * Event description: Event triggered after review trying to be published, but content box is empty.
 *
 * @param {bmReplySubmissionWarningParams} params - The parameters for this event
 * @property {string} review_id - review guid
 * @property {string} tab_name - published / pending / rejected
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmReplySubmissionWarningParams>} LogObject<bmReplySubmissionWarningParams>
 */
export function bmReplySubmissionWarning(params) {
    return { evid: 8076, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmDeleteReplyModalCancelAction' event (evid: 8077)
 *
 * Event description: Sent when a user press cancel or X in Delete reply modal
 *
 * @param {bmDeleteReplyModalCancelActionParams} params - The parameters for this event
 * @property {GUID} review_id - review_id
 * @property {string} origin - X / Cancel
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmDeleteReplyModalCancelActionParams>} LogObject<bmDeleteReplyModalCancelActionParams>
 */
export function bmDeleteReplyModalCancelAction(params) {
    return { evid: 8077, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmImportReviewsClick' event (evid: 8078)
 *
 * Event description: Event triggered when user clicks Import Reviews in BM
 *
 * @param {bmImportReviewsClickParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @return {LogObject<bmImportReviewsClickParams>} LogObject<bmImportReviewsClickParams>
 */
export function bmImportReviewsClick(params) {
    return { evid: 8078, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'bmImportReviewsAction' event (evid: 8079)
 *
 * Event description: Event triggered when user selects one of the options in Import Reviews process (Download an example CSV or Fill and upload CSV file)
 *
 * @param {bmImportReviewsActionParams} params - The parameters for this event
 * @property {string} errorMessage - this field is filled with error message when error occur. Example: "no_file_is_attached"
 * @property {string} section - upload_csv / import_reviews / finish
 * @property {GUID} app_id - app_id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - site id - msid
 * @property {string} action - download_csv / upload_csv / follow_guidelines / replace / x / cancel / continue / upload_new_csv / back_to_upload / import_reviews / view_errors / learn_how_to_fix
 * @return {LogObject<bmImportReviewsActionParams>} LogObject<bmImportReviewsActionParams>
 */
export function bmImportReviewsAction(params) {
    return { evid: 8079, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'fileUploaded' event (evid: 8510)
 *
 * Event description: Event triggered when a user tries to upload a CSV reviews file
 *
 * @param {fileUploadedParams} params - The parameters for this event
 * @property {string} file_ext - File extension
 * @property {boolean} success - 0 - upload failed / 1 - upload successful
 * @property {string} file_id - The file identifier (null if not successful)
 * @property {string} errorMessage - null if succesful
 * @property {GUID} biToken - metasite id
 * @property {number} file_size - The size of the file being uploaded (in bytes)
 * @property {string} type - initial_upload / replace_upload
 * @return {LogObject<fileUploadedParams>} LogObject<fileUploadedParams>
 */
export function fileUploaded(params) {
    return { evid: 8510, src: 69, endpoint: 'livesite-reviews', params: params };
}
/**
 * A function to create a log object for the 'errorReviewsImport' event (evid: 8511)
 *
 * Event description: whenever an error is happening after trying to import reviews
 *
 * @param {errorReviewsImportParams} params - The parameters for this event
 * @property {GUID} biToken - msid
 * @property {number} num_reviews_failed_mandatory_field - num_reviews_failed_mandatory_field
 * @property {number} num_reviews_total - num_reviews_total
 * @property {number} num_reviews_failed_input_length - num_reviews_failed_input_length
 * @property {string} error_message - error message (file row + error type)
 * @property {number} num_reviews_failed_url_format - num_reviews_failed_url_format
 * @property {number} num_reviews_failed_total - num_reviews_failed_total
 * @property {number} num_reviews_failed_media_count - num_reviews_failed_media_count
 * @property {number} num_reviews_failed_date_format - num_reviews_failed_date_format
 * @property {number} num_reviews_failed_header - num_reviews_failed_header
 * @property {number} num_reviews_success - num_reviews_success
 * @return {LogObject<errorReviewsImportParams>} LogObject<errorReviewsImportParams>
 */
export function errorReviewsImport(params) {
    return { evid: 8511, src: 69, endpoint: 'livesite-reviews', params: params };
}
//# sourceMappingURL=index.js.map